import { useMemo } from 'react';
import { DeviceTypeId } from '../../../constants/deviceTypes';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  groupId?: number;
  connectedTo?: number;
  deviceTypes?: {
    id: number;
    name: string;
    icon?: string;
  };
  settings?: Record<string, any>;
}

interface DeviceHierarchy {
  /** All host devices (deviceTypes.id === HOST) */
  hostDevices: Device[];
  /** Map of host device ID to array of child devices */
  devicesByHost: Record<number, Device[]>;
}

/**
 * Hook to organize devices into a parent-child hierarchy
 * based on the connectedTo relationship
 */
export function useDeviceHierarchy(devices: Device[] | null | undefined): DeviceHierarchy {
  return useMemo(() => {
    if (!devices || devices.length === 0) {
      return { hostDevices: [], devicesByHost: {} };
    }

    // Identify host devices (type === HOST)
    const hostDevices = devices.filter(
      (d) => d.deviceTypes?.id === DeviceTypeId.HOST
    );

    // Build parent-child map using connectedTo
    const devicesByHost: Record<number, Device[]> = {};

    devices.forEach((device) => {
      if (device.connectedTo) {
        if (!devicesByHost[device.connectedTo]) {
          devicesByHost[device.connectedTo] = [];
        }
        devicesByHost[device.connectedTo].push(device);
      }
    });

    return { hostDevices, devicesByHost };
  }, [devices]);
}

/**
 * Get latest value from a metrics array
 */
export function getLatestMetricValue(
  deviceMetrics: Record<number, Record<string, any[]>>,
  deviceId: number,
  key: string
): number | null {
  const series = deviceMetrics[deviceId]?.[key];
  if (!series || series.length === 0) return null;
  const value = series[series.length - 1];
  return typeof value === 'number' ? value : null;
}

/**
 * Get metrics series for a device
 */
export function getMetricSeries(
  deviceMetrics: Record<number, Record<string, any[]>>,
  deviceId: number,
  key: string
): any[] {
  return deviceMetrics[deviceId]?.[key] || [];
}

/**
 * Collect metrics from all child devices connected to a parent
 */
export function getChildMetrics(
  parentDeviceId: number,
  devices: Device[],
  deviceMetrics: Record<number, Record<string, any[]>>
): Record<number, Record<string, any[]>> {
  const childMetrics: Record<number, Record<string, any[]>> = {};

  devices
    .filter((d) => d.connectedTo === parentDeviceId)
    .forEach((child) => {
      if (deviceMetrics[child.id]) {
        childMetrics[child.id] = deviceMetrics[child.id];
      }
    });

  return childMetrics;
}

export default useDeviceHierarchy;
